import { Controller, useFormContext } from 'react-hook-form';
import { useTheme } from '@mui/material';

import { assignEditableScorecardProperties } from './Panes/Create/Scorecard/components/util';
import { ATSConnectHiringRequestCandidateFeedbackTestIds } from 'data-testids/ATS';
import { RowCTAContainer, StyledLink } from './styles';
import { RowCTAProps } from '../types';
import { useDeleteHiringRequestReference, useDeleteHiringRequestScorecard } from './mutations';
import { useQueryGetScorecardsForHiringRequest } from './Panes/List/queries';
import { CandidateFeedbackModalTypes } from '../enum';
import auth from 'utils/auth';
import { useState } from 'react';
import { DeletionModal } from 'sharedComponents/Modal/DeletionModal';
import { ErrorSnackbar } from 'sharedComponents/Snackbar';
import { getAxiosErrorMessageByStatusCode } from 'hooks/errorHandling/errorHandlingHooks';

export const CurrentRowCTAModal = {
  DELETION: 'deletion',
  ERROR: 'error',
} as const;

export type CurrentRowCTAModalType = (typeof CurrentRowCTAModal)[keyof typeof CurrentRowCTAModal];

export const RowCTA: React.FC<RowCTAProps> = ({
  collectionId,
  isComplete,
  id,
  onEditScorecardClick,
  modalType,
}) => {
  const [currentModal, setCurrentModal] = useState<CurrentRowCTAModalType | null>(null);
  const { watch } = useFormContext();
  const candidateId = watch('candidate_id');
  const theme = useTheme();
  const districtId = auth.getUser()?.profile.district.id;
  const { data: completedScorecards } = useQueryGetScorecardsForHiringRequest(id, {
    enabled: modalType === CandidateFeedbackModalTypes.SCORECARD,
  });

  const { mutate: deleteScorecard, error: requestError } = useDeleteHiringRequestScorecard(
    districtId,
    {
      onSuccess: () => {
        setCurrentModal(null);
      },
      onError: () => {
        setCurrentModal(null);
      },
    }
  );
  const requestErrorMessage = getAxiosErrorMessageByStatusCode(requestError);

  const { mutate: deleteReference, error: deleteError } = useDeleteHiringRequestReference({
    onSuccess: () => {
      setCurrentModal(null);
    },
    onError: () => {
      setCurrentModal('error');
    },
  });
  const deleteErrorMessage = getAxiosErrorMessageByStatusCode(deleteError);
  const { setValue } = useFormContext();

  const handleDeleteClick = () => {
    if (modalType === CandidateFeedbackModalTypes.SCORECARD) {
      deleteScorecard({ id, scorecardId: collectionId });
    } else {
      deleteReference({ referenceId: collectionId, candidateId });
    }
  };

  const handleEditClick = (_event, onChange) => {
    const completedScorecard = completedScorecards.find(
      (completedScorecard) => completedScorecard.id === collectionId
    );

    onChange(collectionId);
    onEditScorecardClick(collectionId);

    if (completedScorecard) {
      assignEditableScorecardProperties(completedScorecard, setValue);
    }
  };

  const handleOpenDeletionModal = () => setCurrentModal(CurrentRowCTAModal.DELETION);

  const errorMessage = deleteErrorMessage || requestErrorMessage;

  return (
    <RowCTAContainer>
      <Controller
        name="completed_scorecard_id"
        render={({ field: { onChange } }) => (
          <StyledLink
            sx={{ paddingRight: theme.spacing(3) }}
            onClick={(event) => handleEditClick(event, onChange)}
            data-testid={ATSConnectHiringRequestCandidateFeedbackTestIds.REMIND_OR_EDIT_BUTTON}
          >
            {isComplete ? 'Edit' : 'Finish'}
          </StyledLink>
        )}
      />

      {isComplete && (
        <StyledLink
          onClick={handleOpenDeletionModal}
          data-testid={ATSConnectHiringRequestCandidateFeedbackTestIds.DELETE_BUTTON}
        >
          Delete
        </StyledLink>
      )}
      <DeletionModal
        onClose={() => {
          setCurrentModal(null);
        }}
        onDelete={handleDeleteClick}
        open={currentModal === CurrentRowCTAModal.DELETION}
        text={`You will not be able to recover this ${modalType} once it is deleted.`}
        title="Are you sure?"
      />
      <ErrorSnackbar
        open={currentModal === CurrentRowCTAModal.ERROR}
        message={errorMessage}
        onClose={() => setCurrentModal(null)}
        dataTestId={ATSConnectHiringRequestCandidateFeedbackTestIds.ERROR_TOAST_ROW_CTA}
      />
    </RowCTAContainer>
  );
};
